<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { Plus } from 'lucide-vue-next';
// shadcn-vue 컴포넌트
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Button } from '@/components/shadcnui/button';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Textarea } from '@/components/shadcnui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcnui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcnui/dialog';
// store / composable
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useRole } from '@/composable/useRole';
import { useLogin } from '@/composable/useLogin';
// 타입
import type { role, dataTable, userRole } from '@/types/types';

const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadRoleList, onAddRole, onUpdateRole, onDeleteRole } = useRole();
const { applyRole } = useLogin();

type PermAction = 'add' | 'edit' | 'delete';
const PERMISSION_RESOURCES: { key: string; menuKey: string; label: string; actions: PermAction[] }[] = [
  { key: 'release', menuKey: 'releases', label: '릴리즈', actions: ['add', 'edit', 'delete'] },
  { key: 'releasenote', menuKey: 'releases-note', label: ' 릴리스 노트', actions: ['edit'] },
  { key: 'app', menuKey: 'apps', label: '앱', actions: ['add', 'edit', 'delete'] },
  { key: 'fw', menuKey: 'firmwares', label: '펌웨어', actions: ['add', 'edit', 'delete'] },
  { key: 'chipset', menuKey: 'chipsets', label: '칩셋', actions: ['add', 'edit', 'delete'] },
  { key: 'model', menuKey: 'models', label: '모델', actions: ['add', 'edit', 'delete'] },
  { key: 'fwtype', menuKey: 'firmware-types', label: '펌웨어 타입', actions: ['add', 'edit', 'delete'] },
  { key: 'pkgtype', menuKey: 'package-types', label: '패키지 타입', actions: ['add', 'edit', 'delete'] },
  { key: 'pkg', menuKey: 'packages', label: '패키지', actions: ['add', 'edit', 'delete'] },
  { key: 'priority', menuKey: 'priorities', label: '우선순위', actions: ['add', 'edit', 'delete'] },
];

// 상태
const isLoading = ref(true);
const isDialogOpen = ref(false);
const isUpdateMode = ref(false);
const isFormInvalid = computed(() => {
  return !form.value.name.trim();
});
// 1 = Admin, 2 = custom
const addAdmin = ref(0); 

// Admin 선택 시 모든 권한 체크, Custom 선택 시 기존 권한 유지(업데이트 모드)
watch(addAdmin, (newVal: number) => {
  if (newVal === 1 && !isUpdateMode.value) {
    // Admin 선택 시 모든 권한 체크
    permissionsForm.value = {
      add: PERMISSION_RESOURCES.map(r => r.key),
      edit: PERMISSION_RESOURCES.map(r => r.key),
      delete: PERMISSION_RESOURCES.map(r => r.key),
    };
    hiddenMenusForm.value = PERMISSION_RESOURCES.map(r => r.menuKey);
  }
  else  {
    // Custom 선택 시 기존 권한 유지 (업데이트 모드에서만)
    const currentRole = RSStore.roleList.find(r => r.id === form.value.id);
    permissionsForm.value = currentRole?.permissions
      ? {
        add: currentRole.permissions.dataTable.add ?? [],
        edit: currentRole.permissions.dataTable.edit ?? [],
        delete: currentRole.permissions.dataTable.delete ?? [],
      }
      : { add: [], edit: [], delete: [] };
    hiddenMenusForm.value = currentRole?.permissions.menus ?? [];
  } 
});

// 추가/수정 공용 폼
const emptyPermissions = (): dataTable => ({ add: [], edit: [], delete: [] });
const form = ref({
  id: 0,
  name: '',
  description: ''
});

const permissionsForm = ref<dataTable>(emptyPermissions());
const hiddenMenusForm = ref<string[]>([]);

// 다이얼로그 핸들러
const resetForm = () => {
  form.value = { id: 0, name: '', description: '' };
  addAdmin.value = 0;
  permissionsForm.value = emptyPermissions();
  hiddenMenusForm.value = [];
};
const openAddDialog = () => {
  isUpdateMode.value = false;
  resetForm();
  isDialogOpen.value = true;
};
const openUpdateDialog = (role: role) => {
  isUpdateMode.value = true;
  form.value = {
    id: role.id,
    name: role.name,
    description: role.description,
  };
  addAdmin.value = role.permissions.admin
  permissionsForm.value = role.permissions
    ? {
      add: role.permissions.dataTable.add ?? [],
      edit: role.permissions.dataTable.edit ?? [],
      delete: role.permissions.dataTable.delete ?? [],
    }
    : emptyPermissions();
  hiddenMenusForm.value = role.permissions.menus ?? [];

  isDialogOpen.value = true;
};

// Role 객체 생성
const buildRole = (): userRole => {
  return { admin: addAdmin.value, dataTable: permissionsForm.value, menus: hiddenMenusForm.value };
};

// ADD/UPDATE/DELETE API 호출 핸들러
const handleAdd = async () => {

  await onAddRole(form.value.name, buildRole(), form.value.description,);
  isDialogOpen.value = false;
};
const handleUpdate = async () => {
  await onUpdateRole(form.value.id, form.value.name, buildRole(), form.value.description);
  const foundRole = RSStore.roleList.find(role => role.id === UserStore.role)

  if (foundRole) await applyRole(foundRole.permissions.admin, foundRole.permissions.dataTable, foundRole.permissions.menus)
  isDialogOpen.value = false;
};
const handleDelete = async () => {
  await onDeleteRole(form.value.id);
  isDialogOpen.value = false;
};

const handleSubmit = () => (isUpdateMode.value ? handleUpdate() : handleAdd());

// 마운트 시 데이터 로드
onMounted(async () => {
  await onReadRoleList().finally(() => {
    isLoading.value = false;
  });
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-foreground">권한관리</h1>
      <Button @click="openAddDialog">
        <Plus class="mr-2 h-4 w-4" />
        Add
      </Button>
    </div>

    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50 hover:bg-muted/50">
            <TableHead class="font-semibold px-5">Name</TableHead>
            <TableHead class="font-semibold ">Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태 -->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="3" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <!-- 데이터 목록 -->
          <template v-else>
            <TableRow v-for="role in RSStore.roleList" :key="role.id" class="cursor-pointer hover:bg-accent/50"
              @click="openUpdateDialog(role)">
              <TableCell class="px-5">{{ role.name }}</TableCell>
              <TableCell class="px-5">{{ role.description }}</TableCell>
            </TableRow>
            <TableRow v-for="i in Math.max(0, UserStore.itemPerPage - RSStore.userList.length)" :key="'empty-' + i">
              <TableCell colspan="3">&nbsp;</TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>

    <!-- 추가 / 수정 공용 다이얼로그 -->
    <Dialog v-model:open="isDialogOpen" >
     <DialogContent class="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{{ isUpdateMode ? '권한 수정' : '권한 추가' }}</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div class="py-4 space-y-4 text-foreground">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <Label class="text-sm text-muted-foreground">Name</Label>
              <Input class="text-sm font-medium" v-model="form.name" />
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">Role</Label>
              <Select v-model="addAdmin">
                <SelectTrigger class="">
                  <SelectValue placeholder="역할 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem :value="1">Admin</SelectItem>
                  <SelectItem :value="2">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
            </div>
          </div>
          <div>
            <Label class="text-sm text-muted-foreground">Description</Label>
            <Textarea class="text-sm font-medium w-full min-h-[100px]" v-model="form.description" />
          </div>
          <div class="space-y-3">
            <!-- 메뉴 체크박스 -->
            <Label class="text-sm text-muted-foreground">Menus</Label>
            <div class="grid grid-cols-3 gap-2 rounded-lg border p-3">
              <label v-for="r in PERMISSION_RESOURCES" :key="r.menuKey" class="flex items-center gap-2 text-sm">
                <input type="checkbox" :value="r.menuKey" v-model="hiddenMenusForm" />
                {{ r.label }}
              </label>
            </div>

            <!-- 위에서 체크된 메뉴만 표시 -->
            <Label class="text-sm text-muted-foreground">Permissions</Label>
            <div class="rounded-lg border">
              <div class="grid grid-cols-7 gap-2 border-b bg-muted/50 px-4 py-2 text-sm font-medium">
                <div>데이터</div>
                <div class="text-center col-span-2">Add</div>
                <div class="text-center col-span-2">Edit</div>
                <div class="text-center col-span-2">Delete</div>
              </div>
              <template v-for="r in PERMISSION_RESOURCES" :key="r.key">
                <div v-if="hiddenMenusForm.includes(r.menuKey)"
                  class="grid grid-cols-7 items-center gap-2 border-b px-4 py-2 text-sm last:border-b-0">
                  <div>{{ r.label }}</div>
                  <div class="text-center col-span-2">
                    <input v-if="r.actions.includes('add')" type="checkbox" :value="r.key"
                      v-model="permissionsForm.add" />
                    <span v-else class="text-muted-foreground">—</span>
                  </div>
                  <div class="text-center col-span-2" >
                    <input v-if="r.actions.includes('edit')" type="checkbox" :value="r.key"
                      v-model="permissionsForm.edit" />
                      <span v-else class="text-muted-foreground">—</span>
                  </div>
                  <div class="text-center col-span-2">
                    <input v-if="r.actions.includes('delete')" type="checkbox" :value="r.key"
                      v-model="permissionsForm.delete" />
                    <span v-else class="text-muted-foreground">—</span>
                  </div>
                </div>
              </template>
              <div v-if="hiddenMenusForm.length === 0" class="px-4 py-3 text-sm text-muted-foreground text-center">
                위에서 메뉴를 체크하면 권한이 여기 표시됩니다.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter class="gap-2">
          <Button @click="handleSubmit" :disabled="addAdmin === 0 || isFormInvalid">
            {{ isUpdateMode ? 'Update' : 'Add' }}
          </Button>
          <Button v-if="isUpdateMode" variant="destructive" @click="handleDelete">
            Delete
          </Button>
          <Button variant="outline" @click="isDialogOpen = false">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped>

</style>